import {cx} from '@emotion/css';
import React, {useEffect, createRef, RefObject, MutableRefObject, useRef, ReactElement} from 'react';
import {AccordionItemProps} from '@fo/shared-types';
import {fonts} from '@fo/shared/styles/variables';
import Headers, {HeaderType} from '@fo/designSystem/Text/Headers/Headers';
import AccordionItemIcon, {
  AccordionItemIconVariation,
} from '@fo/designSystem/Accordion/AccordionItemIcon/AccordionItemIcon';

import styles from '../Accordion.styles';

interface FullProps extends AccordionItemProps {
  handleItemCollapsedState: () => void;
  isOpen: boolean;
  labelClass?: string;
  itemClass?: string;
  contentClass?: string;
  iconVariation?: AccordionItemIconVariation;
  containerRef?: RefObject<HTMLDivElement>;
}

function AccordionItem({
  children,
  handleItemCollapsedState,
  isOpen,
  label,
  id,
  labelClass,
  itemClass,
  contentClass,
  iconVariation = AccordionItemIconVariation.ARROW,
  containerRef,
}: FullProps): ReactElement {
  const wrapperElRef: RefObject<HTMLDivElement> = createRef();
  const contentElRef: RefObject<HTMLDivElement> = createRef();
  const transitionTimeoutRef: MutableRefObject<number | undefined> = useRef();
  const isFirstRenderRef: MutableRefObject<boolean> = useRef(true);

  /**
   * takes care of the closed -> open transition
   */
  const applyItemOpenedHeight = () => {
    const item: HTMLDivElement | undefined = contentElRef.current || undefined;
    if (!item) {
      return;
    }
    if (isOpen) {
      // Using requestAnimationFrame makes sure that all commands happend before the browser paints
      requestAnimationFrame(() => {
        // Avoid animations so we make absolutely sure the user won't see anything happening
        item.style.transition = 'none';
        // Sets the height to auto so the browser opens the container to the desired size
        item.style.height = 'auto';

        // We grab the height information to use later
        const height = item.offsetHeight;
        // Set the container height back to 0 (remember, nothing was painted yet) to be ready for transitioning
        item.style.height = '0';
        // Reapply the transition
        item.style.transition = 'all 250ms';
        transitionTimeoutRef.current = window.setTimeout(() => {
          // Now that we're ready, we apply the height on the next paint. Otherwise there will be no animation, just a jump.
          item.style.height = `${height}px`;
          // set it back to auto so when the content size changes, the container changes accordingly
          transitionTimeoutRef.current = window.setTimeout(() => {
            item.style.height = 'auto';

            const wrapper = wrapperElRef.current;

            if (wrapper) {
              const container = containerRef && containerRef.current;
              const scrollTarget = container || window;
              const scrollTop = container ? container.scrollTop : window.scrollY;

              // This is equal to spacing.L which is 6rem
              const offset = 60;
              const scrollToValue = wrapper.getBoundingClientRect().top + scrollTop - offset;

              scrollTarget.scrollTo({
                top: scrollToValue,
                behavior: 'smooth',
              });
            }
          }, 300);
        }, 0);
        // No animation has happened but we still need to make sure the page is positioned at the item
      });
    } else {
      item.style.height = `${item.offsetHeight}px`;
      // Now that we're ready, we apply the height on the next paint. Otherwise there will be no animation, just a jump.
      item.style.height = '0';
    }
  };

  useEffect(() => {
    if (!isFirstRenderRef.current) {
      applyItemOpenedHeight();
    }
    isFirstRenderRef.current = false;
    return () => {
      clearTimeout(transitionTimeoutRef.current);
    };
  }, [isOpen]);

  return (
    <div className={cx(styles.item(isOpen), itemClass)} id={id} ref={wrapperElRef}>
      <div
        className={cx(styles.label, labelClass)}
        onClick={() => handleItemCollapsedState()}
        onKeyUp={() => null}
        data-isopen={isOpen}
        role="switch"
        tabIndex={0}
        aria-checked
      >
        <Headers headerType={HeaderType.H3} alignment={fonts.alignment.LEFT}>
          {label}
        </Headers>
        <div className={cx(styles.chevronIcon)}>
          <AccordionItemIcon variation={iconVariation} isOpen={isOpen} />
        </div>
      </div>
      <div
        ref={contentElRef}
        className={cx(styles.content(isOpen), contentClass)}
        style={{height: isOpen ? 'auto' : '0'}}
      >
        {children}
      </div>
    </div>
  );
}

export default AccordionItem;
